import CloseIconV2 from "@/assets/img/modal/close_icon_v2.svg"
import { zodiacIcons } from "@/features/advisors"
import { useChat } from "@/features/chat"
import { usePlans } from "@/features/products"
import {
  getReportById,
  useReportPurchase,
  type ReportId,
} from "@/features/today"
import { useUserZodiac } from "@/features/webapp"
import { cn } from "@/lib/utils"

import type { AppModalComponentProps } from "../config/modal-registry"

export interface ReportPromoCopyV2 {
  reportId: ReportId
  planId: string
  title: string
  description: string
  image: string
  /** Overlay the user's zodiac glyph on the hero (Astrology only). */
  withZodiacOverlay?: boolean
  /** Extra classes on the hero image wrapper (e.g. soulmate overlap). */
  imageClassName?: string
  /** Image flush to the top of the sheet (no top padding). */
  flushTop?: boolean
  /** Show the full hero without cropping (Aura chips sit on the edges). */
  imageContain?: boolean
}

interface ReportPromoModalV2Props extends AppModalComponentProps {
  copy: ReportPromoCopyV2
}

/** Sale-price fallback when the plans API has no price yet. */
const DEFAULT_SALE_PRICE = 6.99

/** Strike-through fallback when the plans API has no `previousPrice`. */
const DEFAULT_WAS_PRICE = 9.99

/**
 * Shared discount sheet for purchasable reports v2 (Figma Popovers_Reports_*_2).
 * Glow border + hero image + special-offer badge + Continue CTA.
 * Sale / was prices from the offer plan (`price` / `previousPrice`).
 */
export function ReportPromoModalV2({ onClose, copy }: ReportPromoModalV2Props) {
  const report = getReportById(copy.reportId)
  const { userMainSubscriptionId } = useChat()
  const { purchase, isReportPurchasing } = useReportPurchase({ userMainSubscriptionId })
  const zodiac = useUserZodiac()
  const ZodiacIcon = zodiacIcons[zodiac]
  const { plans } = usePlans([copy.planId])

  const isLoading = report ? isReportPurchasing(report) : false
  const salePrice = plans[0]?.price ?? DEFAULT_SALE_PRICE
  const wasPrice = plans[0]?.previousPrice ?? DEFAULT_WAS_PRICE

  if (!report) return null

  const handleContinue = async () => {
    const ok = await purchase(report, copy.planId)
    if (ok) onClose()
  }

  return (
    <div
      data-slot="report-promo-modal-v2"
      className="fixed inset-0 z-[1200] mx-auto flex min-h-[100dvh] w-full max-w-app flex-col justify-end bg-dark-base/80 backdrop-blur-[10px]"
    >
      <div
        className={cn(
          "relative flex w-full flex-col items-center gap-4 overflow-hidden rounded-t-3xl bg-dark-base pb-4",
          "shadow-[0_0_16px_rgba(255,83,127,0.6),0_0_32px_#9565fd]",
          copy.flushTop ? "pt-0" : "pt-4",
        )}
      >
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          disabled={isLoading}
          className="absolute right-4 top-4 z-10 flex size-6 items-center justify-center disabled:opacity-50"
        >
          <img src={CloseIconV2} alt="" className="size-6" />
        </button>

        <div
          className={cn(
            "relative w-full shrink-0 overflow-hidden",
            copy.imageClassName ?? "h-[290px]",
          )}
        >
          <img
            src={copy.image}
            alt=""
            className={cn(
              "pointer-events-none absolute inset-0 size-full",
              copy.imageContain ? "object-contain" : "object-cover",
            )}
          />
          {copy.withZodiacOverlay ? (
            <ZodiacIcon
              aria-label={zodiac}
              className="absolute left-1/2 top-[80px] size-[120px] -translate-x-1/2 text-white [filter:drop-shadow(0_0_12px_rgba(255,255,255,0.45))]"
            />
          ) : null}
        </div>

        <div className="relative z-[1] flex w-full flex-col items-center gap-4 px-4">
          <div className="flex w-full flex-col items-center gap-2">
            <div className="flex h-[30px] items-center rounded-[15px] bg-green/20 px-3 py-2">
              <p className="font-body text-[10px] font-medium uppercase text-green">
                Very special offer!
              </p>
            </div>

            <div className="flex w-full flex-col items-center">
              <h2 className="w-full text-center font-display text-2xl font-medium text-purple-light">
                {copy.title}
              </h2>
              <div className="flex items-center gap-2">
                <span className="font-display text-2xl font-medium text-purple-light">just for</span>
                <span className="relative inline-grid">
                  <span className="font-display text-2xl font-medium text-purple-light/40">
                    ${wasPrice.toFixed(2)}
                  </span>
                  <span
                    aria-hidden
                    className="absolute left-0 top-1/2 h-0.5 w-full -translate-y-1/2 bg-peach"
                  />
                </span>
                <span className="font-display text-2xl font-medium text-purple-light">
                  ${salePrice.toFixed(2)}
                </span>
              </div>
            </div>

            <p className="w-full text-center font-body text-base text-purple-light/70">
              {copy.description}
            </p>
          </div>

          <div className="flex w-full flex-col items-center gap-2">
            <button
              type="button"
              onClick={() => void handleContinue()}
              disabled={isLoading}
              className="flex h-14 w-full items-center justify-center gap-2 rounded-[80px] bg-gradient-to-b from-peach to-purple-accent font-body text-base font-semibold text-white disabled:bg-none disabled:bg-purple-light/10 disabled:text-purple-light/70"
            >
              {isLoading ? (
                <>
                  <span className="size-5 animate-spin rounded-full border-2 border-purple-light border-t-transparent" />
                  Purchasing...
                </>
              ) : (
                "Continue"
              )}
            </button>
            <p className="font-body text-sm text-purple-light/40">
              One-time payment is non-refundable
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}
